import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@component/card";
import { Button } from "@component/button";
import {
    Loader2Icon,
    XIcon,
    CheckCircle2Icon,
    ChevronUpIcon,
    ChevronDownIcon,
    FileTextIcon,
    RefreshCwIcon,
} from "lucide-react";
import { cn } from "@util/tailwind";
import { useKbUpload } from "@context/kb_upload_context";
import { useAiqmUpload } from "@context/aiqm_upload_context";
import { useUserProfileContext } from "@context/user_profile";

export default function KbProcessingToast() {
    const navigate = useNavigate();
    const [, userInfo] = useUserProfileContext();
    const { uploadState, isToastVisible, toastFlashCount, dismissToast, retryFile, removeFile } =
        useKbUpload();
    const { isToastVisible: aiqmVisible } = useAiqmUpload();
    const [isExpanded, setIsExpanded] = useState(true);
    const [showBlueBorder, setShowBlueBorder] = useState(false);

    useEffect(() => {
        if (toastFlashCount === 0) return;
        setShowBlueBorder(true);
        const t = window.setTimeout(() => setShowBlueBorder(false), 1500);
        return () => window.clearTimeout(t);
    }, [toastFlashCount]);

    if (!userInfo || !isToastVisible || !uploadState) return null;

    const { files, phase, uploadProgress } = uploadState;

    const doneCount = files.filter((f) => f.status === "done").length;
    const failedCount = files.filter((f) => f.status === "failed").length;
    const totalCount = files.length;
    const isTerminal = phase === "done";

    const headerLabel = (() => {
        if (phase === "uploading") {
            return totalCount > 1
                ? `Uploading ${totalCount} Documents`
                : "Uploading Document";
        }
        return failedCount > 0 ? "Upload Complete (with errors)" : "Upload Complete";
    })();

    const subRowText = (() => {
        if (phase === "uploading") {
            const remaining = files.filter((f) => f.status === "uploading").length;
            return remaining === 1 ? "1 file uploading…" : `${remaining} files uploading…`;
        }
        if (failedCount > 0) return `${doneCount} uploaded · ${failedCount} failed`;
        return totalCount === 1 ? "1 document uploaded" : `${totalCount} documents uploaded`;
    })();

    const failedFiles = files.filter((f) => f.status === "failed");
    const activeFiles = files.filter((f) => f.status !== "failed");

    return (
        <div
            className={cn(
                "fixed bottom-6 z-50 animate-in slide-in-from-bottom-5 duration-300 transition-[right]",
                aiqmVisible ? "right-[456px]" : "right-6",
            )}
        >
            <Card
                className={cn(
                    "w-[420px] shadow-xl border-border bg-background transition-all duration-300",
                    showBlueBorder && "ring-2 ring-blue-500 ring-offset-1",
                )}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-muted/30 border-b border-border">
                    <div className="flex items-center gap-2">
                        {isTerminal ? (
                            <CheckCircle2Icon className="w-5 h-5 text-emerald-600" />
                        ) : (
                            <Loader2Icon className="w-5 h-5 text-blue-600 animate-spin" />
                        )}
                        <span className="text-sm font-semibold text-foreground">
                            {headerLabel}
                        </span>
                    </div>
                    <div className="flex items-center gap-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-muted"
                            onClick={() => setIsExpanded((e) => !e)}
                        >
                            {isExpanded ? (
                                <ChevronDownIcon className="w-4 h-4" />
                            ) : (
                                <ChevronUpIcon className="w-4 h-4" />
                            )}
                        </Button>
                        {isTerminal && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 hover:bg-muted"
                                onClick={dismissToast}
                            >
                                <XIcon className="w-4 h-4" />
                            </Button>
                        )}
                    </div>
                </div>

                {/* Sub-header */}
                <div className="flex items-center justify-between px-4 py-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">{subRowText}</span>
                    {isTerminal && doneCount > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-blue-600 hover:text-blue-700"
                            onClick={() => {
                                navigate("/training/knowledge-base");
                                dismissToast();
                            }}
                        >
                            VIEW DOCUMENTS
                        </Button>
                    )}
                </div>

                {/* Body */}
                {isExpanded && (
                    <div className="py-1">
                        {/* Active / done files */}
                        {activeFiles.length > 0 && (
                            <div className="divide-y divide-border max-h-48 overflow-y-auto">
                                {activeFiles.map((f) => (
                                    <div key={f.id} className="flex items-center gap-3 px-4 py-2">
                                        <FileTextIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                        <span
                                            className="text-sm text-foreground truncate flex-1"
                                            title={f.filename}
                                        >
                                            {f.filename}
                                        </span>
                                        {f.status === "done" ? (
                                            <CheckCircle2Icon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                        ) : (
                                            <Loader2Icon className="w-4 h-4 text-blue-500 animate-spin flex-shrink-0" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Failed files */}
                        {failedFiles.length > 0 && (
                            <div className={cn(activeFiles.length > 0 && "border-t border-border")}>
                                <div className="px-4 py-2 bg-muted/30 flex items-center justify-between">
                                    <span className="text-xs font-medium text-muted-foreground">
                                        Failed ({failedFiles.length})
                                    </span>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 text-xs text-blue-600 hover:text-blue-700"
                                        onClick={() => failedFiles.forEach((f) => retryFile(f.id))}
                                    >
                                        RETRY ALL
                                    </Button>
                                </div>
                                <div className="divide-y divide-border max-h-36 overflow-y-auto">
                                    {failedFiles.map((f) => (
                                        <div
                                            key={f.id}
                                            className="flex items-center gap-3 px-4 py-2 hover:bg-muted/30 transition-colors"
                                        >
                                            <FileTextIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                            <span
                                                className="text-sm text-foreground truncate flex-1 min-w-0"
                                                title={f.filename}
                                            >
                                                {f.filename}
                                            </span>
                                            <button
                                                onClick={() => retryFile(f.id)}
                                                title="Retry"
                                                className="p-1 text-blue-500 hover:text-blue-700 rounded hover:bg-blue-50 flex-shrink-0 transition-colors"
                                            >
                                                <RefreshCwIcon className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                onClick={() => removeFile(f.id)}
                                                title="Remove"
                                                className="p-1 text-muted-foreground hover:text-destructive rounded hover:bg-muted flex-shrink-0 transition-colors"
                                            >
                                                <XIcon className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Upload progress bar */}
                        {phase === "uploading" && (
                            <div className="px-4 pt-2 pb-3 space-y-1">
                                <div className="flex items-center justify-between text-xs text-muted-foreground">
                                    <span>Uploading…</span>
                                    <span>{uploadProgress}%</span>
                                </div>
                                <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-blue-600 rounded-full transition-all duration-300"
                                        style={{ width: `${uploadProgress}%` }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
}
